import api from './client';
import type { ApiResponse } from '@gokrew/models/generic';
import type {
  TeamsInstallUrl,
  TeamsReconcileResult,
  TeamsStatus,
} from '@/types/teams';

const endpoints = {
  base: (companyId: string) => `/companies/${companyId}/teams-bot`,
  status: (companyId: string) => `${endpoints.base(companyId)}/status`,
  oauthInstall: (companyId: string) =>
    `${endpoints.base(companyId)}/oauth/install`,
  reconcile: (companyId: string) => `${endpoints.base(companyId)}/reconcile`,
  disconnect: (companyId: string) => `${endpoints.base(companyId)}/disconnect`,
  tenantLink: (companyId: string) => `${endpoints.base(companyId)}/tenant/link`,
  adminLinkAccount: (companyId: string) =>
    `/teams/companies/${companyId}/admin/link-account`,
};

// ─── connection ──────────────────────────────────────────────────────────────

export async function getTeamsStatus(companyId: string) {
  const { data } = await api.get<ApiResponse<TeamsStatus>>(
    endpoints.status(companyId)
  );
  return data;
}

/** Starts "Sign in with Microsoft"; the returned URL is a browser redirect. */
export async function getTeamsSignInUrl(companyId: string) {
  const { data } = await api.post<ApiResponse<TeamsInstallUrl>>(
    endpoints.oauthInstall(companyId)
  );
  return data;
}

/** Re-checks the connection against the bot activity we have already seen. */
export async function reconcileTeams(companyId: string) {
  const { data } = await api.post<ApiResponse<TeamsReconcileResult>>(
    endpoints.reconcile(companyId)
  );
  return data;
}

export async function disconnectTeams(companyId: string) {
  const { data } = await api.delete<ApiResponse<null>>(
    endpoints.disconnect(companyId)
  );
  return data;
}

// ─── linking ─────────────────────────────────────────────────────────────────

/** Completes the deep-link flow the bot started in Teams. */
export async function linkTeamsTenant(companyId: string, teamsToken: string) {
  const { data } = await api.post(endpoints.tenantLink(companyId), {
    teamsToken,
  });
  return data;
}

export async function linkTeamsAdminAccount(
  companyId: string,
  teamsToken: string
) {
  const { data } = await api.post(endpoints.adminLinkAccount(companyId), {
    teamsToken,
  });
  return data;
}
